import clsx from 'clsx';
import SpiritoryLabel from './SpiritoryLabel';
import React, { useRef, useState, useEffect } from 'react';
import { CiSearch } from 'react-icons/ci';
import { BiErrorCircle } from 'react-icons/bi';

export default function SpiritoryInput(
  props: React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & {
    isPhone?: boolean;
    label?: string;
    rightIcon?: React.ReactNode;
    searchIcon?: boolean;
    leftIcon?: React.ReactNode;
    error?: string;
  }
) {
  const {
    isPhone,
    label,
    rightIcon,
    searchIcon,
    leftIcon,
    error,
    value,
    onChange,
    defaultValue,
    ...rest
  } = props;

  const [inputValue, setInputValue] = useState(value || '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    if (inputRef.current) {
      const observer = new MutationObserver(() => {
        if (inputRef.current) {
          const newValue = inputRef.current.value;
          if (newValue !== inputValue) {
            setInputValue(newValue);
            if (onChange) {
              onChange({
                target: { value: newValue },
              } as React.ChangeEvent<HTMLInputElement>);
            }
          }
        }
      });

      observer.observe(inputRef.current, {
        attributes: true,
        attributeFilter: ['value'],
      });

      return () => observer.disconnect();
    }
  }, [inputValue, onChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (onChange) {
      onChange(e);
    }
  };

  const valueProps = defaultValue
    ? {
        defaultValue: defaultValue,
      }
    : {
        value: inputValue,
        onChange: handleChange,
      };

  const inputElement = (
    <input
      {...rest}
      {...valueProps}
      ref={inputRef}
      className={clsx(
        'tw-w-full tw-border-2 tw-border-solid tw-border-platinum-gray tw-bg-white tw-py-2.5 tw-font-plusJakartaSans tw-font-medium tw-leading-[22.68px] tw-text-[#232323] tw-outline-none tw-ring-2 tw-ring-transparent focus:tw-border-2 focus:tw-ring-2 disabled:tw-cursor-not-allowed disabled:tw-opacity-50',
        'focus:tw-border-main-shades-500 focus:tw-ring-main-shades-500',
        isPhone ? 'tw-rounded-br-xl tw-rounded-tr-xl tw-pl-14 tw-pr-4' : 'tw-rounded-xl tw-px-4',
        rightIcon && 'tw-pr-12',
        searchIcon && 'tw-pl-10',
        leftIcon && 'tw-pl-10',
        props.className
      )}
    />
  );

  if (label) {
    return (
      <div className="tw-flex tw-w-full tw-flex-col tw-gap-3">
        <SpiritoryLabel htmlFor={props.id} required={props.required}>
          {label}
        </SpiritoryLabel>
        <div>
          <div className="tw-relative">
            {leftIcon && (
              <div
                className="tw-absolute tw-left-4 tw-top-2/4 tw-z-50 tw-flex -tw-translate-y-2/4 tw-items-center tw-justify-center tw-text-gray-500"
                aria-hidden="true"
              >
                {leftIcon}
              </div>
            )}
            {searchIcon && (
              <div className="tw-absolute tw-left-2 tw-top-2/4 tw-z-50 tw-flex -tw-translate-y-2/4 tw-items-center tw-justify-center tw-text-lg">
                <CiSearch />
              </div>
            )}
            {inputElement}
            {rightIcon && (
              <div
                className="tw-absolute tw-right-5 tw-top-2/4 tw-z-50 tw-flex -tw-translate-y-2/4 tw-items-center tw-justify-center tw-text-gray-500"
                aria-hidden="true"
              >
                {rightIcon}
              </div>
            )}
          </div>
          {error && (
            <div className="tw-mt-1 tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-red-500">
              <BiErrorCircle className="tw-mt-0.5" /> {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="tw-relative">
        {leftIcon && (
          <div
            className="tw-absolute tw-left-4 tw-top-2/4 tw-z-50 tw-flex -tw-translate-y-2/4 tw-items-center tw-justify-center tw-text-gray-500"
            aria-hidden="true"
          >
            {leftIcon}
          </div>
        )}
        {searchIcon && (
          <div className="tw-absolute tw-left-2 tw-top-2/4 tw-z-50 tw-flex -tw-translate-y-2/4 tw-items-center tw-justify-center tw-text-lg">
            <CiSearch />
          </div>
        )}
        {inputElement}
        {rightIcon && (
          <div
            className="tw-absolute tw-right-5 tw-top-2/4 tw-z-50 -tw-translate-y-2/4 tw-text-gray-500"
            aria-hidden="true"
          >
            {rightIcon}
          </div>
        )}
      </div>
      {error && (
        <div className="tw-mt-1 tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-red-500">
          <BiErrorCircle className="tw-mt-0.5" /> {error}
        </div>
      )}
    </div>
  );
}
